import React, { useState, useCallback, useContext, createContext } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@/layout/loader';

/**
 * Loader Context
 * Provides global loading state management across the application
 */

const LoaderContext = createContext();

/**
 * Hook to access loader context
 * @return {Object} Loader context with loading state and control functions
 */
export const useLoader = () => useContext(LoaderContext);

/**
 * LoaderProvider Component
 * Wraps the application to provide global loading state
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @return {JSX.Element} Loader provider
 */
export const LoaderProvider = ({ children }) => {
  const [loadingState, setIsLoading] = useState(false);

  const loading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const notLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  return (
    <LoaderContext.Provider value={{ loadingState, loading, notLoading }}>
      {loadingState && <Loader />}
      {children}
    </LoaderContext.Provider>
  );
};

LoaderProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
